import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import type { CostItem, CostsSummary, ValidationResult } from "../types";
import Tooltip from "./ui/Tooltip";
import { formatDate as formatDateUtc } from "../lib/formatDate";
import AiDisclaimer from "./ui/AiDisclaimer";
import ValidationBadge from "./ui/ValidationBadge";
import { useValidationPoll } from "../lib/useValidationPoll";
import { useFindingActions } from "../lib/useFindingActions";
import FindingsSummaryBar from "./ui/FindingsSummaryBar";
import { computeCostsMath, formatCents, toCents } from "../lib/costsMath";
import { AiMark, VerifiedMark } from "./ui/ProvenanceMark";

// Costs incurred card — mirrors KeyFactsPanel's shape (collapsible,
// cached blob on the case row, Regenerate button that re-runs the AI
// scan). Sits directly below KeyFactsPanel in the case view.

interface Props {
  caseId: string;
}

const CATEGORY_LABELS: Record<CostItem["category"], string> = {
  travel: "Travel",
  medical: "Medical",
  accommodation: "Accommodation",
  replacement: "Replacement",
  legal: "Legal",
  other: "Other",
};

const CONFIDENCE_EMOJI: Record<CostItem["confidence"], string> = {
  high: "🟢",
  medium: "🟡",
  low: "🔴",
};

const CONFIDENCE_LABEL: Record<CostItem["confidence"], string> = {
  high: "High confidence — explicit receipt or invoice",
  medium: "Medium confidence — mentioned in correspondence",
  low: "Low confidence — implied from context",
};

function formatAmount(item: CostItem): string {
  // Integer-cent rendering — no float formatting anywhere in this card.
  return formatCents(toCents(item.amount), currencySymbol(item.currency));
}

function currencySymbol(code: string): string {
  switch (code.toUpperCase()) {
    case "USD":
      return "$";
    case "GBP":
      return "£";
    case "EUR":
      return "€";
    case "CAD":
      return "C$";
    case "AUD":
      return "A$";
    case "JPY":
      return "¥";
    default:
      return code + " ";
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return formatDateUtc(iso, "short") || iso;
}

export default function CostsPanel({ caseId }: Props) {
  const [costs, setCosts] = useState<CostsSummary | null>(null);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validation, setValidation] = useState<ValidationResult | null>(null);

  // Poll for the background Tier-2 result after a re-scan.
  useValidationPoll(
    validation,
    () => api.getCosts(caseId).then((r) => r.validation ?? null),
    setValidation,
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getCosts(caseId);
        if (cancelled) return;
        setCosts(res.costs);
        setValidation(res.validation ?? null);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  const { v: vFindings, onAction: onFindingAction } = useFindingActions(
    validation,
    { caseId, target: "costs" },
  );

  async function regenerate() {
    setRegenerating(true);
    setError(null);
    try {
      const res = await api.regenerateCosts(caseId);
      setCosts(res.costs);
      setValidation(res.validation ?? null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRegenerating(false);
    }
  }

  const hasCosts = !!costs && costs.items.length > 0;

  // ALL arithmetic in integer cents via the shared house math
  // (lib/costsMath.ts — same fold utility as the financial summary). The
  // AI-claimed total_usd is never displayed; an exact component/total
  // relationship in the curated set folds instead of double-counting; if a
  // stated total doesn't verify, NO headline renders.
  const math = useMemo(
    () => (costs ? computeCostsMath(costs.items) : null),
    [costs],
  );

  const dateRange = useMemo(() => {
    if (!costs || costs.items.length === 0) return null;
    const dates = costs.items
      .map((i) => i.date)
      .filter((d): d is string => !!d)
      .sort();
    if (dates.length === 0) return null;
    return { first: dates[0]!, last: dates[dates.length - 1]! };
  }, [costs]);

  const sym = costs?.currency_symbol ?? "$";

  return (
    <section className="costs-panel">
      <header
        className="costs-head"
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
      >
        <span className="costs-caret" aria-hidden>
          {open ? "▾" : "▸"}
        </span>
        <h3>Costs incurred</h3>
        {hasCosts && math?.headlineCents != null && (
          <span className="costs-total-inline">
            {formatCents(math.headlineCents, sym)}
          </span>
        )}
        {costs?.generated_at && (
          <span className="costs-stamp muted">
            Updated {formatDateUtc(costs.generated_at, "short")}
          </span>
        )}
        <div
          className="costs-actions"
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip
            title={
              hasCosts
                ? "Re-scan every record for costs and refresh the total"
                : "Scan every record for costs incurred"
            }
          >
            <button
              type="button"
              className="btn-subtle"
              onClick={regenerate}
              disabled={regenerating}
            >
              {regenerating
                ? "Scanning…"
                : hasCosts
                  ? "Regenerate"
                  : "Scan for costs"}
            </button>
          </Tooltip>
        </div>
      </header>

      {open && (
        <div className="costs-body">
          {loading && (
            <div className="muted" role="status" aria-live="polite">
              Loading…
            </div>
          )}
          {regenerating && !loading && (
            <div
              className="muted"
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              Scanning every record for costs incurred…
            </div>
          )}
          {error && <div className="form-error">{error}</div>}

          {!loading && !costs && !error && (
            <p className="muted">
              Click <strong>Scan for costs</strong> to detect expenses,
              receipts, and out-of-pocket amounts across every record in
              this case.
            </p>
          )}

          {costs && !hasCosts && !error && (
            <p className="muted">
              No costs detected yet. Upload receipts or expense records to
              track financial losses — use the{" "}
              <strong>+ Add → Scan receipt</strong> option in the timeline.
            </p>
          )}

          {hasCosts && (
            <>
              <div className="costs-headline">
                {math?.headlineCents != null ? (
                  <div className="costs-total">
                    <span className="costs-total-label muted">
                      Total costs incurred
                    </span>
                    <span className="costs-total-amount">
                      {formatCents(math.headlineCents, sym)} <VerifiedMark what="Sum of the listed amounts (exact integer-cent arithmetic)" />
                    </span>
                  </div>
                ) : (
                  <div className="costs-total">
                    <span className="costs-total-label muted">
                      Grouped costs (a stated total in this set could not be
                      verified against its items — no computed total shown)
                    </span>
                  </div>
                )}
                <div className="costs-meta muted">
                  <div>
                    {costs.items.length}{" "}
                    {costs.items.length === 1 ? "item" : "items"}
                  </div>
                  {dateRange && (
                    <div>
                      {formatDate(dateRange.first)} —{" "}
                      {formatDate(dateRange.last)}
                    </div>
                  )}
                </div>
              </div>

              {math && math.categoryCents.length > 0 && (
                <ul className="costs-breakdown">
                  {math.categoryCents.map((b) => (
                    <li key={b.category} className="costs-breakdown-row">
                      <span className="costs-breakdown-label">
                        {CATEGORY_LABELS[b.category as CostItem["category"]] ?? b.category}{" "}
                        <AiMark what="Cost grouping" />
                      </span>
                      <span className="costs-breakdown-amount">
                        {formatCents(b.cents, sym)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              <p className="costs-summary-line muted">
                {costs.summary} <AiMark what="Cost curation summary" />
              </p>

              <button
                type="button"
                className="btn-subtle costs-expand-toggle"
                onClick={() => setExpanded((v) => !v)}
              >
                {expanded ? "Hide breakdown" : "View breakdown"}
              </button>

              {expanded && (
                <ul className="costs-items">
                  {costs.items.map((item, i) => (
                    <li
                      key={`${item.record_id ?? "nil"}-${i}`}
                      className="costs-item"
                    >
                      <div className="costs-item-row">
                        <Tooltip title={CONFIDENCE_LABEL[item.confidence]}>
                          <span
                            className="costs-item-confidence"
                            aria-label={CONFIDENCE_LABEL[item.confidence]}
                          >
                            {CONFIDENCE_EMOJI[item.confidence]}
                          </span>
                        </Tooltip>
                        <span className="costs-item-date">
                          {formatDate(item.date)}
                        </span>
                        <span className="costs-item-description">
                          {item.description} <AiMark what="Cost label" />
                          {math?.foldedTotalIndex === i && (
                            <span className="muted">
                              {" "}(stated total — counted once via its
                              components)
                            </span>
                          )}
                        </span>
                        <span className="costs-item-category muted">
                          {CATEGORY_LABELS[item.category]}
                        </span>
                        <span className="costs-item-amount">
                          {formatAmount(item)} <VerifiedMark what="Amount" />
                        </span>
                      </div>
                      {item.record_id && (
                        <div className="costs-item-source muted">
                          Record <code>{item.record_id.slice(0, 8)}</code>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {costs && costs.items && costs.items.length > 0 && (
                <>
                  <ValidationBadge
                    validation={vFindings}
                    verifying={regenerating || !!validation?.tier2_pending}
                    onAction={onFindingAction}
                  />
                  {vFindings && (
                    <FindingsSummaryBar
                      validation={vFindings}
                      onAction={onFindingAction}
                      inlineMappable={false}
                    />
                  )}
                  <AiDisclaimer />
                </>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
